import { navigate } from '@reach/router';
import React, { useState } from 'react';
import styled from 'styled-components/macro';
import { IfUserHasPermission } from '../../../authorisation/IfUserHasPermission';
import { ReactComponent as AddIcon } from '../../../images/icons/add.svg';
import { ReactComponent as AirSourceHeatPumpIcon } from '../../../images/icons/air-source-heat-pump.svg';
import { ReactComponent as BoreholeHeatPumpIcon } from '../../../images/icons/borehole-heat-pump.svg';
import { ReactComponent as EyeIcon } from '../../../images/icons/eye.svg';
import { ReactComponent as FileIcon } from '../../../images/icons/file.svg';
import { ReactComponent as GroundLoopsHeatPumpIcon } from '../../../images/icons/ground-loops-heat-pump.svg';
import { ReactComponent as PencilIcon } from '../../../images/icons/pencil.svg';
import { ReactComponent as RefreshIcon } from '../../../images/icons/refresh.svg';
import { ReactComponent as ReviewIcon } from '../../../images/icons/review.svg';
import { ReactComponent as SolarIcon } from '../../../images/icons/solar.svg';
import { ReactComponent as AssignmentIcon } from '../../../images/icons/assignment.svg';
import { ReactComponent as UploadIcon } from '../../../images/icons/upload.svg';
import { MenuButton, MenuButtonItem } from '../../../shared/MenuButton';
import { Td, Tr } from '../../../shared/Table';
import { colourNeutral05 } from '../../../styling/colours';
import { spacing03 } from '../../../styling/spacing';
import { DesignResponse } from '../../projects/projectQuotesAndDesignsResponse';
import { projectUrls } from '../../projects/projectUrls';
import { GenerateBillOfMaterialsForDesignModal } from '../GenerateBillOfMaterialsForDesignModal';
import { UploadDesignToNetSuiteModal } from '../UploadDesignToNetSuiteModal';

type Props = {
  projectId: number;
  design: DesignResponse;
  userCanEditDesigns: boolean;
  refreshDesigns: () => void;
};

export const ProjectDesignRow = (props: Props) => {
  const { projectId, design, userCanEditDesigns, refreshDesigns } = props;

  const [isGenerateBillOfMaterialsModalOpen, setIsGenerateBillOfMaterialsModalOpen] = useState(false);
  const [isUploadToNetSuiteModalOpen, setIsUploadToNetSuiteModalOpen] = useState(false);

  return (
    <>
      <GenerateBillOfMaterialsForDesignModal
        projectId={projectId}
        designId={design.designId}
        isOpen={isGenerateBillOfMaterialsModalOpen}
        closeModal={() => setIsGenerateBillOfMaterialsModalOpen(false)}
        shouldRegenerate={design.hasBillOfMaterials}
      />
      <UploadDesignToNetSuiteModal
        projectId={projectId}
        designId={design.designId}
        isOpen={isUploadToNetSuiteModalOpen}
        closeModal={() => setIsUploadToNetSuiteModalOpen(false)}
        refreshDesigns={refreshDesigns}
      />
      <Tr key={design.designId}>
        <Td>
          {design.name}
          {design.isDraft && <DraftIndicator>[Draft]</DraftIndicator>}
        </Td>
        <Td>
          <Icons>
            {design.heatPumpType === 'Air Source' && (
              <AirSourceHeatPumpIcon width={40} height={40} title="Air Source" />
            )}
            {design.heatPumpType === 'Ground Source with Ground Loops' && (
              <GroundLoopsHeatPumpIcon width={40} height={40} title="Ground Source with Ground Loops" />
            )}
            {design.heatPumpType === 'Ground Source with Borehole' && (
              <BoreholeHeatPumpIcon width={40} height={40} title="Ground Source with Borehole" />
            )}
            {design.includesSolar && <SolarIcon width={40} height={40} title="Solar" />}
          </Icons>
        </Td>
        <Td />
        <Td />
        <Td />
        <Td>{design.createdDateTime}</Td>
        <Td>{design.createdBy}</Td>
        <Td>{design.uploadedDateTime}</Td>
        <Td>
          <MenuButton upwards={true} testId={projectDesignRowMenuButtonTestId(design.designId)}>
            <MenuButtonItem onClick={() => navigate(projectUrls.editDesign(props.projectId, design.designId))}>
              {userCanEditDesigns ? <PencilIcon /> : <EyeIcon />}
              {userCanEditDesigns ? 'Edit' : 'View'} Design
            </MenuButtonItem>
            <IfUserHasPermission requiredPermission="Edit">
              <MenuButtonItem onClick={() => navigate(projectUrls.newDesignRevision(props.projectId, design.designId))}>
                <AddIcon /> Create New Revision
              </MenuButtonItem>
              {design.hasBillOfMaterials && (
                <MenuButtonItem
                  onClick={() => navigate(projectUrls.editDesignBillOfMaterials(projectId, design.designId))}
                >
                  <ReviewIcon />
                  Edit BOM
                </MenuButtonItem>
              )}
              {!design.isDraft && (
                <MenuButtonItem onClick={() => setIsGenerateBillOfMaterialsModalOpen(true)}>
                  <RefreshIcon />
                  {design.hasBillOfMaterials ? 'Regenerate' : 'Generate'} BOM
                </MenuButtonItem>
              )}
              {design.hasBillOfMaterials && (
                <MenuButtonItem
                  onClick={() => navigate(projectUrls.viewDesignHandoverPack(projectId, design.designId))}
                >
                  <FileIcon />
                  View Handover Pack
                </MenuButtonItem>
              )}
            </IfUserHasPermission>
            {!design.isDraft && (
              <MenuButtonItem
                onClick={() => navigate(projectUrls.dataForDesignDrawings(props.projectId, design.designId))}
              >
                <AssignmentIcon />
                View Data for Design Drawings
              </MenuButtonItem>
            )}
            {!design.isDraft && design.hasBillOfMaterials && (
              <MenuButtonItem
                onClick={() => setIsUploadToNetSuiteModalOpen(true)}
                disabled={design.isMissingRequiredSalesOrder}
                title={design.isMissingRequiredSalesOrder ? uploadToNetSuiteDisabledTitle : undefined}
              >
                <UploadIcon />
                Upload to NetSuite
              </MenuButtonItem>
            )}
          </MenuButton>
        </Td>
      </Tr>
    </>
  );
};

export const projectDesignRowMenuButtonTestId = (designId: number) => `project-design-row-menu-button-${designId}`;

export const uploadToNetSuiteDisabledTitle =
  'Select a Sales Order for every technology in this Design before uploading it to NetSuite';

const Icons = styled.div`
  svg {
    vertical-align: middle;

    &:not(:last-of-type) {
      margin-right: ${spacing03};
    }
  }
`;

export const DraftIndicator = styled.span`
  color: ${colourNeutral05};
  margin-left: ${spacing03};
`;
